import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Loader2, Check, Film, FileText } from "lucide-react"
import { cn } from "@/lib/utils"
import { useScrollSequence } from "./shared"

type ClipState = "hidden" | "loading" | "revealed"

interface ClipCard {
    title: string
    promptSummary: string
    gradient: string
    duration: string
    borderClass: string
}

const CLIPS: ClipCard[] = [
    {
        title: "Scene 1",
        promptSummary: "Wide establishing shot of Tokyo skyline bathed in golden hour light, slow pan right",
        gradient: "from-amber-400 via-orange-500 to-rose-500",
        duration: "5.0s",
        borderClass: "border-green-200",
    },
    {
        title: "Scene 2",
        promptSummary: "Close-up of neon signs reflecting in rain puddles, shallow depth of field",
        gradient: "from-cyan-400 via-blue-500 to-purple-600",
        duration: "5.0s",
        borderClass: "border-green-200",
    },
]

const SEQUENCE_STEPS: Array<{ clipIndex: number; state: ClipState }> = [
    { clipIndex: 0, state: "loading" },
    { clipIndex: 1, state: "loading" },
    { clipIndex: 0, state: "revealed" },
    { clipIndex: 1, state: "revealed" },
]

const SEQUENCE = [
    { delay: 300 },
    { delay: 500 },
    { delay: 800 },
    { delay: 400 },
]

const INITIAL_STATES: ClipState[] = CLIPS.map(() => "hidden")

export function CodeReviewBlock() {
    const [clipStates, setClipStates] = useState<ClipState[]>([...INITIAL_STATES])

    const { ref } = useScrollSequence(
        SEQUENCE,
        (i) => {
            const { clipIndex, state } = SEQUENCE_STEPS[i]
            setClipStates((prev) => {
                const next = [...prev]
                next[clipIndex] = state
                return next
            })
        },
        () => setClipStates([...INITIAL_STATES]),
        { amount: 0.7, margin: "40px 0px -40px 0px" },
    )

    return (
        <div
            ref={ref}
            className="relative flex items-center justify-center px-6 md:px-12 pt-6 md:pt-12 pb-2 overflow-visible"
        >
            <div className="grid grid-cols-2 gap-3 w-full">
                {CLIPS.map((clip, i) => (
                    <AnimatePresence key={i}>
                        {clipStates[i] !== "hidden" && (
                            <motion.div
                                initial={{ opacity: 0, y: 16, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.35, ease: "easeOut" }}
                                className={cn(
                                    "bg-white rounded-md border-2 overflow-hidden",
                                    clipStates[i] === "revealed" ? clip.borderClass : "border-blue-200"
                                )}
                            >
                                <div className="aspect-video bg-neutral-100 relative w-full group cursor-pointer">
                                    <div className={cn(
                                        "absolute inset-0 bg-gradient-to-br",
                                        clip.gradient,
                                        clipStates[i] === "loading" && "opacity-30"
                                    )} />

                                    <AnimatePresence>
                                        {clipStates[i] === "loading" && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="absolute inset-0 bg-black/10 flex items-center justify-center"
                                            >
                                                <Loader2 size={14} className="animate-spin text-neutral-500" />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                    <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-medium px-1 py-0.5 rounded">{i + 1}</div>
                                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1 py-0.5 rounded">{clip.duration}</div>
                                    <div className="absolute bottom-1 left-1">
                                        <span className="bg-neutral-600/90 text-white px-1.5 py-0.5 rounded text-[9px] font-medium leading-none inline-flex items-center">
                                            <FileText size={9} />
                                        </span>
                                    </div>
                                </div>

                                <div className="p-2.5">
                                    <p className="text-xs text-neutral-500 line-clamp-2 leading-relaxed">{clip.promptSummary}</p>
                                    <div className="flex items-center justify-between mt-2">
                                        <AnimatePresence mode="wait">
                                            {clipStates[i] === "loading" ? (
                                                <motion.div key="rendering" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                                    className="relative inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded overflow-hidden bg-blue-50">
                                                    <Loader2 size={10} className="animate-spin text-blue-700" />
                                                    <span className="ml-0.5 text-blue-700">Rendering</span>
                                                </motion.div>
                                            ) : (
                                                <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                                    className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">
                                                    <Check size={10} />
                                                    <span className="ml-0.5">Done</span>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                        <div className="text-neutral-400 p-1 rounded">
                                            <Film size={12} />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                ))}
            </div>
        </div>
    )
}
